import React, { memo, useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import Image from 'react-bootstrap/Image';
import Carousel from 'react-bootstrap/Carousel';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import RessourceApi from '../api/RessourceApi';
import Ressource from '../modeles/Ressource';
import IMAGES_ACTUALITES from '../constantes/actualites/ImagesActualites';
import EvenementsContainer from './EvenementsContainer';
import Don from '../composants/Don';
import Adresse from '../composants/Adresse';
import { obtenirImageDepuisRessource } from '../utils/ressourceImageUtils';
import ImageGenerique from '../composants/ImageGenerique';
import DescriptionGenerique from '../composants/DescriptionGenerique';
import '../fichierCss/Accueil.css';
import '../fichierCss/Actualites.css';
import { useNavigate } from 'react-router-dom';

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(2, 1),
    marginTop: theme.spacing(1.5),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3, 1),
      marginTop: theme.spacing(2),
    },
  },
  card: {
    borderRadius: 0,
    overflow: 'hidden',
    boxShadow: 'none',
    padding: 0,
    height: '100%',
    background: 'transparent',
  },
  sectionTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1.5),
    color: '#333',
  },
  carouselMedia: {
    width: '100%',
    height: 'auto',
    borderRadius: '8px',
    display: 'block',
  },
  carouselFrame: {
    width: '99%',
    maxWidth: '1000px',
    margin: '0 auto 0 0',
    height: '100%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      margin: '0 auto',
    },
  },
  rowBottom: {
    marginTop: theme.spacing(2),
  },
  addressCard: {
    borderRadius: '10px',
    background: '#fafafa',
    border: '1px solid #ececec',
  },
  mediaShell: {
    width: '100%',
    minHeight: 260,
    maxHeight: 420,
    overflow: 'hidden',
    marginBottom: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    background: '#f1f7fb',
    border: '1px solid rgba(15, 45, 63, 0.1)',
  },
  mediaTitle: {
    marginTop: theme.spacing(1.25),
    marginBottom: theme.spacing(0.5),
    fontWeight: 700,
    fontSize: '1.35rem',
    color: '#12354a',
    textAlign: 'center',
    width: '100%',
  },
  descriptionWrap: {
    marginTop: theme.spacing(1.2),
    padding: theme.spacing(1.5, 1.25),
    borderRadius: 8,
    background: '#ffffff',
    border: '1px solid rgba(15, 45, 63, 0.1)',
    maxHeight: 230,
    overflowY: 'auto' as 'auto',
  },
}));

const ActualiteContainer = memo(() => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [actualites, setActualites] = useState([]);
  const [imageEvenement, setImageEvenement] = useState<string>('');
  const [titreEvenement, setTitreEvenement] = useState('');
  const [descriptionEvenement, setDescriptionEvenement] = useState('');
  const [evenementSelectionne, setEvenementSelectionne] = useState(false);

  const handleSelect = (selectedIndex) => {
    setIndex(selectedIndex);
  };

  const getImage = (itemIndex) => {
    const actualite = actualites[itemIndex];
    const fallback = IMAGES_ACTUALITES[itemIndex] || IMAGES_ACTUALITES[0] || '';
    return obtenirImageDepuisRessource(actualite?.cheminFichier, 'Actualite', fallback);
  };

  const ouvrirContenuActualite = (itemIndex) => {
    const actualite = actualites[itemIndex];
    if (!actualite) {
      return;
    }

    navigate('/contenu-generique', {
      state: {
        contenu: {
          type: 'Actualite',
          id: actualite.id,
          titre: actualite.titre,
          description: actualite.description,
          image: getImage(itemIndex),
        },
      },
    });
  };

  const ouvrirContenuEvenement = (evenement: Ressource) => {
    if (!evenement) return;
    const imageUrl = obtenirImageDepuisRessource(evenement.cheminFichier, 'Evenement', '');
    setImageEvenement(imageUrl || '');
    setTitreEvenement(evenement.titre || '');
    setDescriptionEvenement(evenement.description || evenement.resume || '');
    setEvenementSelectionne(true);
  };

  useEffect(() => {
    let mounted = true;

    const fetchActualites = async () => {
      try {
        const data = await RessourceApi.obtenirRessourcesParCategorie('Actualite');
        if (!mounted) return;
        setActualites(Array.isArray(data) ? data.map((item) => item instanceof Ressource ? item : new Ressource(item)) : []);
      } catch (error) {
        console.error('Erreur chargement actualités:', error);
        setActualites([]);
      }
    };

    fetchActualites();
    return () => {
      mounted = false;
    };
  }, []);

  // Définir une hauteur partagée pour garantir l'égalité stricte
  const TOP_GRID_HEIGHT = 720;
  return (
    <div className={classes.root}>
      <Grid container spacing={3} alignItems="stretch" style={{ minHeight: TOP_GRID_HEIGHT, height: TOP_GRID_HEIGHT, width: '100%', margin: 0 }}>
        <Grid item xs={12} md={8} lg={8} style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', height: TOP_GRID_HEIGHT}}>
          <Paper className={classes.card} style={{height: '100%', minHeight: TOP_GRID_HEIGHT, maxHeight: TOP_GRID_HEIGHT, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', padding: 0, margin: 0, width: '100%'}}>
            <div style={{flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', height: TOP_GRID_HEIGHT, width: '100%'}}>
              {!evenementSelectionne ? (
                <div className={classes.carouselFrame}>
                  <Carousel activeIndex={index} onSelect={handleSelect} style={{height: TOP_GRID_HEIGHT, width: '100%'}}>
                    {actualites.map((actualite, itemIndex) => (
                      <Carousel.Item
                        key={actualite?.id ?? itemIndex}
                        style={{height: '100%', width: '100%', cursor: 'pointer'}}
                        onClick={() => ouvrirContenuActualite(itemIndex)}
                      >
                        <Image
                          className={`${classes.carouselMedia} image rounded-corner`}
                          src={getImage(itemIndex)}
                          alt={actualite?.titre || `Actualité ${itemIndex + 1}`}
                          rounded
                          style={{height: '110%', width: '100%', objectFit: 'cover'}}
                          onError={(e) => {
                            const img = e.currentTarget as HTMLImageElement;
                            const fallback = IMAGES_ACTUALITES[itemIndex % IMAGES_ACTUALITES.length] || IMAGES_ACTUALITES[0] || '';
                            if (fallback && img.src !== fallback) {
                              img.src = fallback;
                            } else {
                              img.style.display = 'none';
                            }
                          }}
                        />
                        <Carousel.Caption>
                          <h3>{actualite?.titre}</h3>
                          <p>{actualite?.description ? `${actualite.description.substring(0, 50)}...` : ''}</p>
                        </Carousel.Caption>
                      </Carousel.Item>
                    ))}
                  </Carousel>
                </div>
              ) : (
                <div style={{width: '100%', padding: '16px'}}>
                  <div className={classes.mediaShell}>
                    <ImageGenerique key={imageEvenement} image={imageEvenement} titre={titreEvenement} style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                  </div>
                  <div className={classes.mediaTitle}>{titreEvenement}</div>
                  <Divider style={{margin: '8px 0'}} />
                  <div className={classes.descriptionWrap}>
                    <DescriptionGenerique description={descriptionEvenement} />
                  </div>
                  <button
                    onClick={() => setEvenementSelectionne(false)}
                    style={{marginTop: 12, background: 'none', border: '1px solid #0f766e', color: '#0f766e', borderRadius: 4, padding: '4px 14px', cursor: 'pointer', fontSize: '0.85rem'}}
                  >
                    &#8592; Retour aux actualités
                  </button>
                </div>
              )}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4} lg={4} style={{display: 'flex', flexDirection: 'column', height: TOP_GRID_HEIGHT}}>
          <Paper className={classes.card} style={{height: '100%', minHeight: TOP_GRID_HEIGHT, maxHeight: TOP_GRID_HEIGHT, display: 'flex', flexDirection: 'column', padding: 0, margin: 0}}>
            <div style={{flex: 1, minHeight: 0, overflowY: 'auto'}}>
              <EvenementsContainer variant="list" onEvenementClick={ouvrirContenuEvenement} />
            </div>
          </Paper>
        </Grid>
      </Grid>

      {/* Rangée Don et Adresse alignés en bas */}
      <Grid container spacing={3} className={classes.rowBottom} alignItems="stretch" style={{ marginTop: 32 }}>
        <Grid item xs={12} md={6} style={{display: 'flex'}}>
          <Paper className={classes.card} style={{minHeight: 180, height: '100%', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
            <h2 className={classes.sectionTitle}>Faire un don</h2>
            <Divider style={{ marginBottom: '12px' }} />
            <Don />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6} style={{display: 'flex'}}>
          <Paper className={`${classes.card} ${classes.addressCard}`} id="map" style={{minHeight: 180, height: '100%', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
            <h2 className={classes.sectionTitle}>Adresse</h2>
            <Divider style={{ marginBottom: '12px' }} />
            <Adresse/>
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
});

export default ActualiteContainer;